import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { EventTypes, OidcSecurityService, PublicEventsService } from 'angular-auth-oidc-client';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { filter, firstValueFrom, Observable, skip, Subscription } from 'rxjs';
import { AppService } from 'app/app.service';
import { TENANT_CONTEXT_HEADER } from 'app/auth/tenant-context.interceptor';

export interface RequirementIntakeChangedEvent {
  readonly type: 'REQUIREMENT_INTAKE_CHANGED';
  readonly revisionId: number;
}

@Injectable({ providedIn: 'root' })
export class RequirementIntakeEventsService {
  private static readonly BACKGROUND_TAB_DISCONNECT_GRACE_PERIOD_MS = 60_000;

  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly publicEventsService = inject(PublicEventsService);
  private readonly appService = inject(AppService);
  private readonly connectionId = globalThis.crypto.randomUUID();

  // Observable created at class level so toObservable() runs inside the injection context.
  private readonly adminTenantId$ = toObservable(this.appService.adminTenantId);

  private buildEventsUrl(revisionId: number): string {
    const queryParameters = new URLSearchParams({
      revisionId: revisionId.toString(),
      connectionId: this.connectionId,
    });

    return `/api/app/events/requirement-intake?${queryParameters.toString()}`;
  }

  private tenantHeaders(): Record<string, string> {
    const tenantId = this.appService.adminTenantId();
    return tenantId ? { [TENANT_CONTEXT_HEADER]: tenantId } : {};
  }

  listenForRevision(revisionId: number): Observable<RequirementIntakeChangedEvent> {
    return new Observable<RequirementIntakeChangedEvent>((subscriber) => {
      let abortController: AbortController | undefined;
      let authEventsSubscription: Subscription | undefined;
      let tenantChangeSubscription: Subscription | undefined;
      let backgroundDisconnectTimeout: ReturnType<typeof setTimeout> | undefined;
      let pendingDisconnectPromise: Promise<void> | undefined;
      let closed = false;
      let reconnectInProgress = false;
      let connectionPausedBecauseTabHidden = false;
      let tabWasHidden = false;

      const closeCurrentConnection = (): void => {
        abortController?.abort();
        abortController = undefined;
      };

      const unregisterCurrentConnection = async (): Promise<void> => {
        try {
          const accessToken = await firstValueFrom(this.oidcSecurityService.getAccessToken());

          if (!accessToken) {
            return;
          }

          const response = await fetch(this.buildEventsUrl(revisionId), {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              ...this.tenantHeaders(),
            },
            keepalive: true,
          });

          if (!response.ok) {
            console.warn(`Requirement intake SSE unregister failed: ${response.status}`);
          }
        } catch (error) {
          console.warn('Requirement intake SSE unregister failed', error);
        }
      };

      const clearBackgroundDisconnectTimeout = (): void => {
        if (backgroundDisconnectTimeout === undefined) {
          return;
        }

        clearTimeout(backgroundDisconnectTimeout);
        backgroundDisconnectTimeout = undefined;
      };

      const disconnectCurrentConnection = (): Promise<void> => {
        closeCurrentConnection();

        const disconnectPromise = unregisterCurrentConnection().finally(() => {
          if (pendingDisconnectPromise === disconnectPromise) {
            pendingDisconnectPromise = undefined;
          }
        });

        pendingDisconnectPromise = disconnectPromise;
        return disconnectPromise;
      };

      const scheduleBackgroundDisconnect = (): void => {
        if (backgroundDisconnectTimeout !== undefined) {
          return;
        }

        backgroundDisconnectTimeout = setTimeout(() => {
          backgroundDisconnectTimeout = undefined;

          if (closed || document.visibilityState !== 'hidden') {
            return;
          }

          connectionPausedBecauseTabHidden = true;
          void disconnectCurrentConnection();
        }, RequirementIntakeEventsService.BACKGROUND_TAB_DISCONNECT_GRACE_PERIOD_MS);
      };

      const reconnectAfterDisconnectCompleted = async (): Promise<void> => {
        await pendingDisconnectPromise;

        if (closed) {
          return;
        }

        connectionPausedBecauseTabHidden = false;
        await connect();
      };

      const connect = async (): Promise<void> => {
        if (closed || reconnectInProgress || connectionPausedBecauseTabHidden) {
          return;
        }

        reconnectInProgress = true;
        let currentAbortController: AbortController | undefined;

        try {
          const accessToken = await firstValueFrom(this.oidcSecurityService.getAccessToken());

          if (closed) {
            return;
          }

          closeCurrentConnection();

          currentAbortController = new AbortController();
          abortController = currentAbortController;

          await fetchEventSource(this.buildEventsUrl(revisionId), {
            headers: {
              Accept: 'text/event-stream',
              Authorization: `Bearer ${accessToken}`,
              ...this.tenantHeaders(),
            },
            signal: currentAbortController.signal,
            openWhenHidden: true,
            onopen: async (response) => {
              if (response.status === 401 || response.status === 403) {
                throw new Error(`Requirement intake SSE authorization failed: ${response.status}`);
              }

              if (!response.ok) {
                throw new Error(`Requirement intake SSE connection failed: ${response.status}`);
              }

              const contentType = response.headers.get('content-type') ?? '';
              if (!contentType.toLowerCase().includes('text/event-stream')) {
                throw new Error(
                  `Requirement intake SSE returned unexpected content type: ${contentType}`,
                );
              }
            },
            onmessage: (event) => {
              if (event.event !== 'requirement-intake-changed') {
                return;
              }

              try {
                subscriber.next(JSON.parse(event.data));
              } catch (error) {
                subscriber.error(error);
              }
            },
            onerror: (error) => {
              console.warn('Requirement intake SSE connection error', error);

              // Returning undefined keeps fetchEventSource's default retry behavior.
              // A token refresh event explicitly recreates the connection with a fresh Authorization header.
            },
          });
        } catch (error) {
          if (currentAbortController?.signal.aborted) {
            return;
          }

          if (!closed) {
            subscriber.error(error);
          }
        } finally {
          reconnectInProgress = false;
        }
      };

      const reconnectWithFreshToken = (): void => {
        closeCurrentConnection();
        void connect();
      };

      const visibilityChangeListener = (): void => {
        if (document.visibilityState === 'hidden') {
          tabWasHidden = true;
          scheduleBackgroundDisconnect();
          return;
        }

        clearBackgroundDisconnectTimeout();

        if (!tabWasHidden || closed) {
          return;
        }

        tabWasHidden = false;

        if (!connectionPausedBecauseTabHidden) {
          return;
        }

        void reconnectAfterDisconnectCompleted();

        // Force a reload when tab becomes visible as we might have missed some events
        subscriber.next({
          type: 'REQUIREMENT_INTAKE_CHANGED',
          revisionId,
        });
      };

      document.addEventListener('visibilitychange', visibilityChangeListener);

      authEventsSubscription = this.publicEventsService
        .registerForEvents()
        .pipe(filter((event) => event.type === EventTypes.NewAuthenticationResult))
        .subscribe(() => {
          if (closed || document.visibilityState === 'hidden' || connectionPausedBecauseTabHidden) {
            return;
          }

          reconnectWithFreshToken();
        });

      // Close and reconnect with fresh headers whenever the active tenant changes.
      // skip(1) avoids an immediate reconnect on first subscribe.
      tenantChangeSubscription = this.adminTenantId$.pipe(skip(1)).subscribe(() => {
        if (closed || connectionPausedBecauseTabHidden) {
          return;
        }

        reconnectWithFreshToken();
      });

      void connect();

      return () => {
        closed = true;
        clearBackgroundDisconnectTimeout();
        document.removeEventListener('visibilitychange', visibilityChangeListener);
        authEventsSubscription?.unsubscribe();
        tenantChangeSubscription?.unsubscribe();
        void disconnectCurrentConnection();
      };
    });
  }
}
